import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Typography,
  Box,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import http from '../../http-common';
import BudgetTypeService from '../../services/budgetType.service';
import BudgetUnitService from '../../services/budgetUnit.service';
import { useTranslation } from '../../hooks/useTranslation';

const ADD_NEW_TYPE_VALUE = '__add_new_type__';
const ADD_NEW_UNIT_VALUE = '__add_new_unit__';

const BudgetForm = () => {
  const [budgetTypes, setBudgetTypes] = useState([]);
  const [budgetUnits, setBudgetUnits] = useState([]);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [budget, setBudget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    status: 'draft',
    valid_until: null,
    approved_at: null
  });
  const [recipients, setRecipients] = useState([
    { id: Date.now(), name: '', email: '', source: 'manual', enabled: true }
  ]);
  const [sendOnSubmit, setSendOnSubmit] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendRecipientsModal, setSendRecipientsModal] = useState([{ id: Date.now(), email: '' }]);
  const [emailMessage, setEmailMessage] = useState('');
  const [storeEmails, setStoreEmails] = useState(true);
  const [updateStatusToSent, setUpdateStatusToSent] = useState(true);
  const [addTypeDialogOpen, setAddTypeDialogOpen] = useState(false);
  const [addUnitDialogOpen, setAddUnitDialogOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newUnitName, setNewUnitName] = useState('');
  const [addingForItemId, setAddingForItemId] = useState(null);
  const [addingSubmitting, setAddingSubmitting] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useTranslation();

  useEffect(() => {
    fetchBudgetTypes();
    fetchBudgetUnits();
    fetchClients();
    
    if (isEdit) {
      fetchBudget();
    } else {
      // Add initial empty item
      setItems([{
        id: Date.now(),
        line_number: 1,
        description: '',
        budget_type_id: '',
        quantity: 1,
        budget_unit_id: '',
        unit_price: 0,
        total_price: 0,
        notes: ''
      }]);
      setRecipients([{ id: Date.now(), name: '', email: '', source: 'manual', enabled: true }]);
    }
  }, [id, isEdit]);

  useEffect(() => {
    const selectedClient = (clients || []).find(client => client.id === formData.client_id);
    setRecipients(prev => {
      const withoutClient = prev.filter(recipient => recipient.source !== 'client');
      if (selectedClient?.email) {
        const existing = prev.find(recipient => recipient.source === 'client');
        return [
          ...withoutClient,
          {
            id: 'client-recipient',
            name: selectedClient.name || 'Client',
            email: selectedClient.email,
            source: 'client',
            enabled: existing?.enabled ?? true,
          }
        ];
      }
      return withoutClient.length > 0
        ? withoutClient
        : [{ id: Date.now(), name: '', email: '', source: 'manual', enabled: true }];
    });
  }, [formData.client_id, clients]);

  const fetchBudgetTypes = async () => {
    try {
      const response = await http.get('/budget-types');
      setBudgetTypes(response.data?.data || []);
    } catch (error) {
      console.error('Error loading budget types:', error);
      setBudgetTypes([]);
      showSnackbar(t('budgets.errorLoadingBudgetTypes'), 'error');
    }
  };

  const fetchBudgetUnits = async () => {
    try {
      const response = await http.get('/budget-units');
      console.log('unidades',response.data?.data);
      setBudgetUnits(response.data?.data || []);
    } catch (error) {
      console.error('Error loading budget units:', error);
      setBudgetUnits([]);
      showSnackbar(t('budgets.errorLoadingBudgetUnits'), 'error');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await http.get('/clients');
      setClients(response.data?.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
      showSnackbar(t('budgets.errorLoadingClients'), 'error');
    }
  };

  const fetchBudget = async () => {
    setLoading(true);
    try {
      const response = await http.get(`/budgets/${id}`);
      const budgetData = response.data.data;
      console.log('budgetData',budgetData);
      setBudget(budgetData);
      
      setFormData({
        name: budgetData.name,
        description: budgetData.description || '',
        client_id: budgetData.client_id || '',
        status: budgetData.status,
        valid_until: budgetData.valid_until ? dayjs(budgetData.valid_until) : null,
        approved_at: budgetData.approved_at ? dayjs(budgetData.approved_at) : null
      });

      const existingRecipients = budgetData.recipients || budgetData.budget_recipients || [];
      const normalizedRecipients = existingRecipients.map((recipient) => ({
        id: recipient.id,
        name: recipient.name || '',
        email: recipient.email || '',
        source: recipient.source || 'manual',
        enabled: true
      }));

      if (budgetData.client?.email && !normalizedRecipients.some(recipient => recipient.source === 'client')) {
        normalizedRecipients.push({
          id: 'client-recipient',
          name: budgetData.client.name || 'Client',
          email: budgetData.client.email,
          source: 'client',
          enabled: true,
        });
      }

      if (normalizedRecipients.length === 0) {
        normalizedRecipients.push({ id: Date.now(), name: '', email: '', source: 'manual', enabled: true });
      }

      setRecipients(normalizedRecipients);

      // Handle both snake_case and camelCase naming
      const budgetItems = budgetData.budget_items || budgetData.budgetItems || [];
      
      // If no items exist, create one empty item
      if (budgetItems.length === 0) {
        setItems([{
          id: Date.now(),
          line_number: 1,
          description: '',
          budget_type_id: '',
          quantity: 1,
          budget_unit_id: '',
          unit_price: 0,
          total_price: 0,
          notes: ''
        }]);
      } else {
        setItems(budgetItems.map(item => ({
          id: item.id,
          line_number: item.line_number,
          description: item.description,
          budget_type_id: item.budget_type_id,
          quantity: item.quantity,
          budget_unit_id: item.budget_unit_id,
          unit_price: item.unit_price,
          total_price: item.total_price,
          notes: item.notes || ''
        })));
      }
    } catch (error) {
      console.error('Error loading budget:', error);
      showSnackbar(t('budgets.errorLoadingBudget'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = async () => {
    if (!newTypeName.trim()) {
      showSnackbar(t('budgets.nameRequired'), 'warning');
      return;
    }
    setAddingSubmitting(true);
    try {
      const res = await BudgetTypeService.create({ name: newTypeName.trim(), is_active: true });
      const created = res.data?.data || res.data;
      await fetchBudgetTypes();
      if (addingForItemId && created?.id) {
        updateItem(addingForItemId, 'budget_type_id', String(created.id));
      }
      setAddTypeDialogOpen(false);
      setNewTypeName('');
      setAddingForItemId(null);
      showSnackbar(t('budgets.budgetTypeCreated'));
    } catch (error) {
      showSnackbar(error.response?.data?.message || t('budgets.errorSavingType'), 'error');
    } finally {
      setAddingSubmitting(false);
    }
  };

  const handleCreateUnit = async () => {
    if (!newUnitName.trim()) {
      showSnackbar(t('budgets.nameRequired'), 'warning');
      return;
    }
    setAddingSubmitting(true);
    try {
      const res = await BudgetUnitService.create({ name: newUnitName.trim(), is_active: true });
      const created = res.data?.data || res.data;
      await fetchBudgetUnits();
      if (addingForItemId && created?.id) {
        updateItem(addingForItemId, 'budget_unit_id', String(created.id));
      }
      setAddUnitDialogOpen(false);
      setNewUnitName('');
      setAddingForItemId(null);
      showSnackbar(t('budgets.budgetUnitCreated'));
    } catch (error) {
      showSnackbar(error.response?.data?.message || t('budgets.errorSavingUnit'), 'error');
    } finally {
      setAddingSubmitting(false);
    }
  };

  const addItem = () => {
    const itemsArray = items || [];
    const newItem = {
      id: Date.now(),
      line_number: itemsArray.length + 1,
      description: '',
      budget_type_id: '',
      quantity: 1,
      budget_unit_id: '',
      unit_price: 0,
      total_price: 0,
      notes: ''
    };
    setItems([...itemsArray, newItem]);
  };

  const removeItem = (itemId) => {
    const itemsArray = items || [];
    if (itemsArray.length === 1) {
      showSnackbar(t('budgets.budgetMustHaveItem'), 'warning');
      return;
    }
    setItems(itemsArray.filter(item => item.id !== itemId));
  };

  const updateItem = (itemId, field, value) => {
    const itemsArray = items || [];
    const updatedItems = itemsArray.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        
        // Calculate total price if quantity or unit_price changes
        if (field === 'quantity' || field === 'unit_price') {
          updatedItem.total_price = updatedItem.quantity * updatedItem.unit_price;
        }
        
        return updatedItem;
      }
      return item;
    });
    setItems(updatedItems);
  };

  const addRecipient = () => {
    setRecipients(prev => [...prev, { id: Date.now(), name: '', email: '', source: 'manual', enabled: true }]);
  };

  const removeRecipient = (recipientId) => {
    setRecipients(prev => {
      if (prev.length === 1) return prev;
      return prev.filter(r => r.id !== recipientId);
    });
  };

  const updateRecipient = (recipientId, field, value) => {
    setRecipients(prev => prev.map(recipient => {
      if (recipient.id === recipientId) {
        const updatedItem = { ...recipient, [field]: value };
        return updatedItem;
      }
      return recipient;
    }));
  };

  const calculateTotals = () => {
    const itemsArray = items || [];
    const subtotal = itemsArray.reduce((sum, item) => {
      const totalPrice = parseFloat(item.total_price) || 0;
      return sum + totalPrice;
    }, 0);
    const total = subtotal;
    
    return { 
      subtotal: Number(subtotal) || 0, 
      total: Number(total) || 0 
    };
  };

  const handleSubmit = async () => {
    try {
      const itemsArray = items || [];
      if (itemsArray.length === 0) {
        showSnackbar(t('budgets.budgetMustHaveItem'), 'error');
        return;
      }

      // Validate all items have required fields
      const invalidItems = itemsArray.filter(item => 
        !item.description || 
        !item.budget_type_id || 
        !item.budget_unit_id ||
        item.quantity <= 0 ||
        item.unit_price < 0
      );

      if (invalidItems.length > 0) {
        console.log('invalidItems',invalidItems);
        showSnackbar(t('budgets.pleaseFillAllItemFields'), 'error');
        return;
      }

      setLoading(true);
      
      const payload = {
        ...formData,
        valid_until: formData.valid_until ? formData.valid_until.format('YYYY-MM-DD') : null,
        approved_at: formData.approved_at ? formData.approved_at.format('YYYY-MM-DD HH:mm:ss') : null,
        items: itemsArray.map((item, index) => ({
          id: item.id && item.id > 1000 ? item.id : undefined, // Only include ID for existing items
          description: item.description,
          budget_type_id: item.budget_type_id,
          quantity: item.quantity,
          budget_unit_id: item.budget_unit_id,
          unit_price: item.unit_price,
          notes: item.notes
        })),
        recipients: recipients
          .filter(recipient => recipient.source !== 'client' && recipient.enabled && recipient.email?.trim())
          .map(recipient => ({
            email: recipient.email.trim(),
            name: recipient.name?.trim() || null,
            source: 'manual'
          })),
        send_email: sendOnSubmit,
      };

      if (isEdit) {
        await http.put(`/budgets/${id}`, payload);
        showSnackbar(t('budgets.budgetUpdatedSuccess'));
      } else {
        await http.post('/budgets', payload);
        showSnackbar(t('budgets.budgetCreatedSuccess'));
      }
      
      setSendOnSubmit(false);
      navigate('/budgets');
    } catch (error) {
      showSnackbar(`Error ${isEdit ? 'updating' : 'creating'} budget`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const openSendDialog = () => {
    if (!isEdit) return;
    const baseRecipients = recipients
      .filter(recipient => recipient.enabled && recipient.email?.trim())
      .map(recipient => ({
        id: recipient.id,
        email: recipient.email.trim(),
        source: recipient.source || 'manual'
      }));

    if (baseRecipients.length > 0) {
      setSendRecipientsModal(baseRecipients);
    } else {
      setSendRecipientsModal([{ id: Date.now(), email: '', source: 'manual' }]);
    }

    setEmailMessage('');
    setStoreEmails(true);
    setSendDialogOpen(true);
  };

  const addSendRecipient = () => {
    setSendRecipientsModal(prev => [...prev, { id: Date.now(), email: '', source: 'manual' }]);
  };

  const updateSendRecipient = (recipientId, value) => {
    setSendRecipientsModal(prev => prev.map(recipient => (
      recipient.id === recipientId ? { ...recipient, email: value } : recipient
    )));
  };

  const removeSendRecipient = (recipientId) => {
    setSendRecipientsModal(prev => {
      if (prev.length === 1) return prev;
      return prev.filter(recipient => recipient.id !== recipientId);
    });
  };

  const confirmSendNow = async () => {
    try {
      const emails = sendRecipientsModal
        .map(recipient => recipient.email?.trim())
        .filter(email => email);

      if (emails.length === 0) {
        showSnackbar(t('budgets.pleaseProvideEmail'), 'error');
        return;
      }

      await http.post(`/budgets/${id}/send-email`, {
        emails,
        message: emailMessage,
        store_emails: storeEmails,
        update_status_to_sent: updateStatusToSent,
      });

      showSnackbar(t('budgets.budgetSentSuccess'));
      setSendDialogOpen(false);
      fetchBudget();
    } catch (error) {
      console.error('Error sending budget:', error);
      showSnackbar(t('budgets.errorSendingBudget'), 'error');
    }
  };

  const { subtotal, total } = calculateTotals();

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate('/budgets')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">
                {isEdit ? t ('budgets.editBudget'): t ('budgets.newBudget')}
              </Typography>
            </Box>
          }
          action={
            <Box display="flex" gap={1}>
              {isEdit && (
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={openSendDialog}
                >
                  {t('budgets.sendNow')}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={loading}
              >
                {isEdit ? t ('common.update') : t ('common.save')}  {t ('budgets.title')}
              </Button>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t ('budgets.nameBudget')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t ('clients.client')}</InputLabel>
                <Select
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  label={t('clients.client')}
                >
                  <MenuItem value="">{t('budgets.noClient')}</MenuItem>
                  {(clients || []).map(client => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {formData.client_id && budget?.client?.email && (
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={recipients.some(r => r.source === 'client' && r.enabled)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRecipients(prev => {
                          const withoutClient = prev.filter(rec => rec.source !== 'client');
                          if (checked) {
                            return [
                              ...withoutClient,
                              {
                                id: 'client-recipient',
                                name: budget.client.name || 'Client',
                                email: budget.client.email,
                                source: 'client',
                                enabled: true,
                              },
                            ];
                          }
                          return withoutClient;
                        });
                      }}
                    />
                  }
                  label={t('budgets.sendToClient', { email: budget.client.email })}
                />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t ('common.status')}</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    if (newStatus === 'sent') {
                      const wantsToSend = window.confirm('Do you want to send this budget by email now?');
                      setSendOnSubmit(wantsToSend);
                      if (wantsToSend && !formData.approved_at) {
                        setFormData(prev => ({ ...prev, approved_at: dayjs() }));
                      }
                    } else {
                      setSendOnSubmit(false);
                      if (newStatus !== 'approved') {
                        setFormData(prev => ({ ...prev, approved_at: null }));
                      } else if (!formData.approved_at) {
                        setFormData(prev => ({ ...prev, approved_at: dayjs() }));
                      }
                    }
                    setFormData({ ...formData, status: newStatus });
                  }}
                  label={t('common.status')}
                >
                  <MenuItem value="draft">{t ('budgets.draft')}</MenuItem>
                  <MenuItem value="sent">{t ('budgets.sent')}</MenuItem>
                  <MenuItem value="approved">{t ('budgets.approved')}</MenuItem>
                  <MenuItem value="rejected">{t ('budgets.rejected')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label={t ('budgets.valid')}
                value={formData.valid_until}
                onChange={(newValue) => setFormData({ ...formData, valid_until: newValue })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            {formData.status === 'approved' && (
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={t('budgets.approvedOn')}
                  value={formData.approved_at}
                  onChange={(newValue) => setFormData({ ...formData, approved_at: newValue })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            )}
          </Grid>

          <TextField
            fullWidth
            label={t ('budgets.description')}
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 3 }}>
            <Typography variant="h6">{t('budgets.emailRecipients')}</Typography>
          </Divider>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {recipients.map((recipient, index) => (
              <Grid item xs={12} key={recipient.id}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={recipient.enabled !== false}
                          onChange={(e) => updateRecipient(recipient.id, 'enabled', e.target.checked)}
                          disabled={recipient.source === 'client'}
                        />
                      }
                      label={t('budgets.sendLabel')}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label={t('budgets.recipientName', { n: index + 1 })}
                      value={recipient.name}
                      onChange={(e) => updateRecipient(recipient.id, 'name', e.target.value)}
                      disabled={recipient.source === 'client'}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required={index === 0}
                      label={t('budgets.recipientEmail', { n: index + 1 })}
                      value={recipient.email}
                      onChange={(e) => updateRecipient(recipient.id, 'email', e.target.value)}
                      placeholder="email@example.com"
                      disabled={recipient.source === 'client'}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      color="error"
                      variant="outlined"
                      onClick={() => removeRecipient(recipient.id)}
                      disabled={recipients.length === 1 || recipient.source === 'client'}
                    >
                      {t('budgets.remove')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button variant="text" onClick={addRecipient}>
                {t('budgets.addRecipient')}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }}>
            <Typography variant="h6">{t('budgets.BudgetItems')}</Typography>
          </Divider>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="60">Line</TableCell>
                  <TableCell>{t ('budgets.description')}</TableCell>
                  <TableCell>{t ('common.type')}</TableCell>
                  <TableCell width="100">{t ('budgets.quantity')}</TableCell>
                  <TableCell>{t ('budgets.unit')}</TableCell>
                  <TableCell width="120">{t ('budgets.unitPrice')}</TableCell>
                  <TableCell width="120">{t ('common.total')}</TableCell>
                  <TableCell width="80">{t ('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(items || []).map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={item.description}
                        onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                        placeholder={t ('common.itemDescription')}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={item.budget_type_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === ADD_NEW_TYPE_VALUE) {
                              setAddingForItemId(item.id);
                              setAddTypeDialogOpen(true);
                              return;
                            }
                            updateItem(item.id, 'budget_type_id', val);
                          }}
                          displayEmpty
                        >
                          <MenuItem value="">{t ('common.selectType')}</MenuItem>
                          {(budgetTypes || []).map(type => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                          <MenuItem value={ADD_NEW_TYPE_VALUE} sx={{ borderTop: 1, borderColor: 'divider' }}>
                            + {t('budgets.addNewType')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? 0 : parseFloat(value);
                          updateItem(item.id, 'quantity', numValue);
                        }}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={item.budget_unit_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === ADD_NEW_UNIT_VALUE) {
                              setAddingForItemId(item.id);
                              setAddUnitDialogOpen(true);
                              return;
                            }
                            updateItem(item.id, 'budget_unit_id', val);
                          }}
                          displayEmpty
                        >
                          <MenuItem value="">{t ('budgets.selectUnit')}</MenuItem>
                          {(budgetUnits || []).map(unit => (
                            <MenuItem key={unit.id} value={unit.id}>
                              {unit.name}
                            </MenuItem>
                          ))}
                          <MenuItem value={ADD_NEW_UNIT_VALUE} sx={{ borderTop: 1, borderColor: 'divider' }}>
                            + {t('budgets.addNewUnit')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.unit_price}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? 0 : parseFloat(value);
                          updateItem(item.id, 'unit_price', numValue);
                        }}
                        inputProps={{ min: 0, step: 1.00 }}
                        InputProps={{
                          startAdornment: <Typography variant="body2">Q</Typography>
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Q {parseFloat(item.total_price).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeItem(item.id)}
                        disabled={(items || []).length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addItem}
            sx={{ mb: 3 }}
          >
          {t ('common.addItem')}
          </Button>

          <Card variant="outlined" sx={{ backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box textAlign="center">
                    <Typography color="textSecondary" gutterBottom>
                      Total
                    </Typography>
                    <Typography variant="h5" color="primary">
                      Q {total.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={addTypeDialogOpen} onClose={() => !addingSubmitting && (setAddTypeDialogOpen(false), setNewTypeName(''), setAddingForItemId(null))} maxWidth="xs" fullWidth>
        <DialogTitle>{t('budgets.newBudgetType')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('common.name')}
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateType()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTypeDialogOpen(false)} disabled={addingSubmitting}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreateType} disabled={addingSubmitting || !newTypeName.trim()}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addUnitDialogOpen} onClose={() => !addingSubmitting && (setAddUnitDialogOpen(false), setNewUnitName(''), setAddingForItemId(null))} maxWidth="xs" fullWidth>
        <DialogTitle>{t('budgets.newBudgetUnit')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('common.name')}
            value={newUnitName}
            onChange={(e) => setNewUnitName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateUnit()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUnitDialogOpen(false)} disabled={addingSubmitting}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreateUnit} disabled={addingSubmitting || !newUnitName.trim()}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('budgets.sendBudgetViaEmail')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t('budgets.sendDialogDescription')}
          </Typography>
          {sendRecipientsModal.map((recipient, index) => (
            <Box key={recipient.id} display="flex" gap={1} alignItems="center" mt={1}>
              <TextField
                fullWidth
                label={t('budgets.emailNumber', { n: index + 1 })}
                value={recipient.email}
                onChange={(e) => updateSendRecipient(recipient.id, e.target.value)}
                placeholder={t('budgets.emailPlaceholder')}
                disabled={recipient.source === 'client'}
              />
              <Button
                color="error"
                variant="outlined"
                onClick={() => removeSendRecipient(recipient.id)}
                disabled={sendRecipientsModal.length === 1 || recipient.source === 'client'}
              >
                {t('budgets.remove')}
              </Button>
            </Box>
          ))}
          <Box mt={1}>
            <Button variant="text" onClick={addSendRecipient}>
              {t('budgets.addRecipient')}
            </Button>
          </Box>
          <TextField
            fullWidth
            label={t('budgets.messageOptional')}
            helperText={t('budgets.emailTemplateHint')}
            multiline
            rows={3}
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={storeEmails}
                  onChange={(e) => setStoreEmails(e.target.checked)}
                />
              }
              label={t('budgets.storeNewEmails')}
            />
          </Box>
          <Box mt={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={updateStatusToSent}
                  onChange={(e) => setUpdateStatusToSent(e.target.checked)}
                />
              }
              label={t('budgets.updateStatusToSent')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={confirmSendNow}>{t('budgets.send')}</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default BudgetForm;